import { useId } from "react";
import type { Gate } from "@helm/core";
import { TRADING_DISABLED_REASON, disabledTooltip } from "@helm/ui";

/**
 * A trading control (SPEC §9.7: tooltips spell out each action).
 *
 * - With `onPress` and a `gate`, it is live when the gate allows it (ADR-0021: mock / Rithmic Test
 *   only, READY, a trading account, risk) and the command is not awaiting its result (`busy`, so
 *   a double click cannot send twice). Otherwise it is disabled and the tooltip says why.
 * - Without `onPress` it is always disabled with the A2 reason (controls not wired yet).
 *
 * Disabled uses `aria-disabled` rather than the `disabled` attribute so it stays focusable and
 * hoverable: keyboard and pointer users both reach the tooltip that says why it is off.
 */
export function TradeButton({
  children,
  semantics,
  hotkey,
  tone = "neutral",
  size = "md",
  className,
  gate,
  busy = false,
  onPress,
  label,
}: {
  children: React.ReactNode;
  /** What the control does. */
  semantics: string;
  hotkey?: string;
  /** `exit`: danger-exit outline (Close, Reverse, Cancel all); `flatten`: danger-exit solid. */
  tone?: "buy" | "sell" | "neutral" | "exit" | "flatten";
  size?: "sm" | "md" | "lg";
  className?: string;
  gate?: Gate;
  busy?: boolean;
  onPress?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Accessible name when the visible text is abbreviated. */
  label?: string;
}): React.JSX.Element {
  const id = useId();
  const live = onPress !== undefined && gate !== undefined;
  const reason = !live
    ? TRADING_DISABLED_REASON
    : !gate.ok
      ? gate.reason
      : busy
        ? "Waiting for the gateway's answer"
        : null;
  const enabled = reason === null;
  const tip = enabled
    ? `${semantics}${hotkey ? ` (${hotkey})` : ""}`
    : disabledTooltip(semantics, hotkey, reason);
  return (
    <button
      type="button"
      className={`tbtn tbtn-${tone} tbtn-${size}${busy ? " tbtn-busy" : ""}${className ? ` ${className}` : ""}`}
      aria-disabled={enabled ? undefined : "true"}
      aria-busy={busy || undefined}
      aria-label={label}
      aria-describedby={id}
      data-tooltip={tip}
      data-gate={live && gate.ok ? "ok" : live && !gate.ok ? gate.code : "A2"}
      onClick={(e) => {
        e.preventDefault();
        if (enabled) onPress?.(e);
      }}
    >
      {children}
      <span id={id} className="sr-only">
        {semantics}.{enabled ? "" : ` ${reason}.`}
      </span>
    </button>
  );
}
